/* eslint-disable camelcase */
import mixpanel from 'mixpanel-browser';
import userflow from 'userflow.js';

import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import axios from 'services/axios';
import { ONBOARDING } from 'services/consts';

import { updateInstructionData } from 'stores/InstructionSlice';

import AppLogger from 'utils/AppLogger';
import { store } from 'utils/configureStore';

let isInit = false;

export const appTrackingData = {
	isNewRegistered: false
};

const RedirectExtension = {
	name: 'Redirect',
	type: 'response',
	match: ({ trace }) => trace.type === 'ext_redirect' || trace.payload?.name === 'ext_redirect',
	render: ({ trace }) => {
		const payload = trace.payload;
		const payloadArr = payload.split('-.-');
		const lang = payloadArr[0];
		const transcript = payloadArr[1];

		const menuNames = {
			ar: 'راسلنا',
			en: 'Chat with us',
			tr: 'Bizimle sohbet edin'
		};

		/**
		 * render function
		 * -trace -> ["time","type","payload","defaultPath","paths"]
		 * -element -> html element
		 */
		window.voiceflow?.chat?.interact({
			type: 'complete',
			payload: { status: 'redirected' }
		});
		window.voiceflow?.chat?.hide();
		window.fcWidget?.open({ name: menuNames[lang] });
		window.fcWidget?.user?.update({
			meta: {
				cf_transcript: transcript
			}
		});
		window.activeChatAgent = true;
	}
};

const useAppTracking = () => {
	const dispatch = useDispatch();
	const userData = useSelector((state) => state.user);

	const init = useCallback(() => {
		if (import.meta.env.VITE_INIT_APP_TRACKING === 'true') {
			try {
				userflow.init(import.meta.env.VITE_USER_FLOW_API_KEY);
			} catch {
				// do nothing
			}

			try {
				mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
					// debug: true,
					track_pageview: true,
					persistence: 'localStorage'
				});

				window.mixpanelTrack = mixpanel.track;
			} catch {
				// do nothing
			}

			isInit = true;
		} else {
			AppLogger.log('Skipping init app tracking.');
		}
	}, []);

	const track = useCallback(async (name, data, trackOnly = undefined) => {
		if (!isInit) {
			AppLogger.log('AppTracking not initialized. Skipping track.');
			return;
		}
		const user = store.getState().user;
		const trackData = { companyId: user?.companyId, ...data };
		if (!trackOnly || trackOnly.includes('userflow')) {
			try {
				if (userflow.isIdentified()) {
					userflow.track(name, trackData);
				}
			} catch {
				// do nothing
			}
		}
		if (!trackOnly || trackOnly.includes('mixpanel')) {
			try {
				mixpanel.track(name, trackData);
			} catch {
				// do nothing
			}
		}
		if (!trackOnly || trackOnly.includes('cio')) {
			try {
				window._cio?.track?.(name, trackData);
			} catch {
				// do nothing
			}
		}
		if (!trackOnly || trackOnly.includes('gtm')) {
			try {
				window.dataLayer.push({
					event: name,
					authenticationMethod: 'email',
					userId: user.ID,
					email: user.email
				});
			} catch {
				// do nothing
			}
		}
	}, []);

	const identify = useCallback(
		async (data) => {
			const isOTOUser = data?.roles?.some((role) => role.roleID === 91) || data.companyId === 1;

			if (data.UsageMode === 'test' || isOTOUser) {
				isInit = false;
				return;
			}

			if (!isInit) {
				AppLogger.log('AppTracking not initialized. Skipping identify.');
				return;
			}

			try {
				// Customerio user identification
				window._cio?.identify?.({
					id: data.ID,
					signed_up_at: data.createdAt,
					email: data.email,
					phone_number: data.phone,
					full_name: data.fullName,
					roles: data.roles,
					locale: data.locale,
					locale_code: data.locale,
					ClientStatus: data.clientStatus,
					language: data.locale.split('_').shift(),
					timezone: data.localization.timezone,
					userType: data.userType,
					isMobile: window.innerWidth < 768,
					windowSize: `${window.innerWidth}x${window.innerHeight}`,
					screenSize: `${window.screen.width}x${window.screen.height}`,
					branchCheck: data.branchCheck,
					orderCheck: data.orderCheck,
					salesChannelCheck: data.salesChannelCheck,
					warehouseCheck: data.warehouseCheck,
					shipmentCheck: data.shipmentCheck,
					walletCheck: data.walletCheck,
					boxDimensionCheck: data.boxDimensionCheck,
					calculatePriceCheck: data.calculatePriceCheck,
					dcConnectionCheck: data.dcConnectionCheck,
					codShipmentCheck: data.codShipmentCheck,
					bankInfoCheck: data.bankInfoCheck,
					isOnboarded: data.isOnboarded,
					country: data.localization.countryISO2,
					company: {
						id: data.companyId,
						name: data.companyName,
						plan_name: data.planName,
						credit: data.remainingAmount
					},
					userCheck: data.userCheck,
					productCheck: data.productCheck,
					inventoryCheck: data.inventoryCheck,
					WMSModuleCheck: data.WMSModuleCheck,
					WMSAdvanceModuleCheck: data.WMSAdvanceModuleCheck,
					modules: data.modules,
					mobileVerified: data.mobileVerified,
					nationalIDVerified: data.nationalIDVerified,
					emailVerified: data.mailVerified,
					crDOCStatus: data.CRDocStatus,
					hasTwoFactor: data.mfaTypes === 'totp',
					cohortAgg: data.cohortAgg,
					cohortIntg: data.cohortIntg
				});
			} catch (error) {
				AppLogger.error(error);
			}

			try {
				// Userflow user identification

				userflow
					.identify(data.ID, {
						signed_up_at: data.createdAt,
						email: data.email,
						phone_number: data.phone,
						full_name: data.fullName,
						ClientStatus: data.clientStatus,
						roles: data.roles.map((role) => role.roleName),
						locale_code: data.locale,
						timezone: data.localization.timezone,
						userType: data.userType,
						isMobile: window.innerWidth < 768,
						windowSize: `${window.innerWidth}x${window.innerHeight}`,
						screenSize: `${window.screen.width}x${window.screen.height}`,
						branchCheck: data.branchCheck,
						orderCheck: data.orderCheck,
						salesChannelCheck: data.salesChannelCheck,
						warehouseCheck: data.warehouseCheck,
						shipmentCheck: data.shipmentCheck,
						walletCheck: data.walletCheck,
						boxDimensionCheck: data.boxDimensionCheck,
						calculatePriceCheck: data.calculatePriceCheck,
						dcConnectionCheck: data.dcConnectionCheck,
						codShipmentCheck: data.codShipmentCheck,
						bankInfoCheck: data.bankInfoCheck,
						isOnboarded: data.isOnboarded,
						country: data.localization.countryISO2,
						userCheck: data.userCheck,
						productCheck: data.productCheck,
						inventoryCheck: data.inventoryCheck,
						WMSModuleCheck: data.WMSModuleCheck,
						WMSAdvanceModuleCheck: data.WMSAdvanceModuleCheck,
						haveCompanyName: data.haveCompanyName,
						showReferLink: data.showReferLink,
						modules: data.modules,
						mobileVerified: data.mobileVerified,
						nationalIDVerified: data.nationalIDVerified,
						emailVerified: data.mailVerified,
						crDOCStatus: data.CRDocStatus,
						cohortAgg: data.cohortAgg,
						cohortIntg: data.cohortIntg,
						hasTwoFactor: data.mfaTypes === 'totp'
					})
					.then(() => {
						window.dispatchEvent(new Event('userflow:identify'));
					});

				userflow.group(
					data.companyId,
					{
						name: data.companyName,
						credit: data.remainingAmount,
						signed_up_at: data.createdAt,
						plan: data.planName,
						haveCompanyName: data.haveCompanyName
					},
					{
						membership: {
							role: data.userType
						}
					}
				);
			} catch (error) {
				AppLogger.error(error);
			}

			try {
				mixpanel.identify(data.ID);
				mixpanel.register({
					id: data.ID,
					companyid: data.companyId
				});
				mixpanel.people.set({
					created: data.createdAt,
					email: data.email,
					phone: data.phone,
					name: data.fullName,
					ClientStatus: data.clientStatus,
					roles: data.roles.map((role) => role.roleName),
					locale_code: data.locale,
					timezone: data.localization.timezone,
					userType: data.userType,
					country_code: data.localization.countryISO2,
					isMobile: window.innerWidth < 768,
					windowSize: `${window.innerWidth}x${window.innerHeight}`,
					screenSize: `${window.screen.width}x${window.screen.height}`,
					branchCheck: data.branchCheck,
					orderCheck: data.orderCheck,
					salesChannelCheck: data.salesChannelCheck,
					warehouseCheck: data.warehouseCheck,
					shipmentCheck: data.shipmentCheck,
					walletCheck: data.walletCheck,
					boxDimensionCheck: data.boxDimensionCheck,
					calculatePriceCheck: data.calculatePriceCheck,
					dcConnectionCheck: data.dcConnectionCheck,
					codShipmentCheck: data.codShipmentCheck,
					bankInfoCheck: data.bankInfoCheck,
					isOnboarded: data.isOnboarded,
					companyName: data.companyName,
					plan: data.planName,
					credit: data.remainingAmount,
					companyId: data.companyId,
					userCheck: data.userCheck,
					productCheck: data.productCheck,
					inventoryCheck: data.inventoryCheck,
					WMSModuleCheck: data.WMSModuleCheck,
					WMSAdvanceModuleCheck: data.WMSAdvanceModuleCheck,
					modules: data.modules,
					mobileVerified: data.mobileVerified,
					emailVerified: data.mailVerified,
					crDOCStatus: data.CRDocStatus,
					hasTwoFactor: data.mfaTypes === 'totp',
					cohortAgg: data.cohortAgg,
					cohortIntg: data.cohortIntg
				});
				mixpanel.add_group('company', data.companyId);
				mixpanel.set_group('company', data.companyId);
				mixpanel.get_group('company', data.companyId).set({
					name: data.companyName,
					credit: data.remainingAmount,
					signed_up_at: data.createdAt,
					plan: data.planName
				});
			} catch (error) {
				AppLogger.error(error);
			}

			try {
				let sentEvent = false;
				let shouldAddDataLayer = false;
				const cohortAgg = data.cohortAgg;
				const cohortAggCheck = data.cohortAggCheck;
				const cohortIntg = data.cohortIntg;
				const cohortIntgCheck = data.cohortIntgCheck;

				if (cohortAgg && cohortAggCheck) {
					const cohortAggValue = Number(cohortAgg.slice(-1));
					const cohortAggCheckValue = Number(cohortAggCheck.slice(-1));

					if (cohortAggValue > cohortAggCheckValue) {
						sentEvent = true;
					}
				} else if (cohortAgg) {
					sentEvent = true;
					shouldAddDataLayer = true;
				}

				if (cohortIntg && cohortIntgCheck) {
					const cohortIntgValue = Number(cohortIntg.slice(-1));
					const cohortIntgCheckValue = Number(cohortIntgCheck.slice(-1));

					if (cohortIntgValue > cohortIntgCheckValue) {
						shouldAddDataLayer = true;
					}
				} else if (cohortIntg) {
					shouldAddDataLayer = true;
				}

				const eventNames = {
					'Cohort 1': '11_shipment',
					'Cohort 2': '101_shipment',
					'Cohort 3': '1001_shipment'
				};

				if (shouldAddDataLayer) {
					if (eventNames[cohortAgg]) {
						window.dataLayer.push({
							event: eventNames[cohortAgg],
							first_name: userData?.firstName || '',
							last_name: userData?.lastName || '',
							phone: userData?.phone || '',
							email: userData?.email || '',
							plan: userData?.planName || '',
							country: userData?.localization?.label || '',
							userID: userData?.ID || ''
						});
					} else if (eventNames[cohortIntg]) {
						window.dataLayer.push({
							event: eventNames[cohortIntg],
							first_name: userData?.firstName || '',
							last_name: userData?.lastName || '',
							phone: userData?.phone || '',
							email: userData?.email || '',
							plan: userData?.planName || '',
							country: userData?.localization?.label || '',
							userID: userData?.ID || ''
						});
					}
				}

				if (sentEvent) {
					window.dataLayer.push({
						event: data.cohortAgg
					});
					await axios
						.post(ONBOARDING.UPDATE_ONBOARDING, {
							cohortAgg
						})
						.then((response) => {
							if (response.status !== 200) {
								AppLogger.error(response);
							}
						})
						.catch((error) => {
							AppLogger.error(error);
						});
				}
			} catch (error) {
				AppLogger.error(error);
			}

			try {
				const initVoiceflow = () => {
					window.voiceflow.chat
						.load({
							verify: {
								projectID: import.meta.env.VITE_VOICEFLOW_KEY
							},
							url: 'https://general-runtime.voiceflow.com/',
							versionID: 'production',
							assistant: {
								extensions: [RedirectExtension]
							},
							launch: {
								event: {
									type: 'launch',
									payload: {
										user_id: data.ID,
										user_email: data.email,
										user_phone: data.phone,
										user_name: data.fullName,
										locale: data.locale,
										user_type: data.userType,
										company_id: data.companyId,
										client_plan: data.planName,
										company_name: data.companyName,
										client_country: data.localization.countryISO2,
										v2_credit: data.remainingAmount,
										client_cohort_agg: data.cohortAgg,
										client_cohort_intg: data.cohortIntg,
										mobileVerified: data.mobileVerified,
										emailVerified: data.mailVerified,
										nationalIDVerified: data.nationalIDVerified
									}
								}
							}
						})
						.then(() => {
							window.voiceflow.chat.hide();
						});
				};

				if (window.voiceflow) {
					initVoiceflow();
					return; // if voiceflow is already loaded, return
				}

				const voiceflowScript = document.createElement('script');
				voiceflowScript.onload = initVoiceflow;
				voiceflowScript.src = 'https://cdn.voiceflow.com/widget-next/bundle.mjs';
				voiceflowScript.type = 'text/javascript';
				document.head.appendChild(voiceflowScript);

				// add event listener for voiceflow chat close
				window.addEventListener(
					'message',
					(event) => {
						if (typeof event.data === 'string') {
							try {
								const type = JSON.parse(event.data).type;

								if (type === 'voiceflow:close') {
									// if chat is closed, open userflow resource center
									try {
										window.voiceflow.chat.hide();
										userflow.openResourceCenter();
									} catch (error) {
										AppLogger.error(error);
									}
								}
							} catch {
								// do nothing
							}
						}
					},
					false
				);
			} catch (error) {
				AppLogger.error(error);
			}

			track('user is identified');

			if (appTrackingData.isNewRegistered) {
				track('User created', { companyId: data?.companyID });
				appTrackingData.isNewRegistered = false;
				if (import.meta.env.VITE_ENVIRONMENT === 'production') {
					try {
						window.dataLayer.push({
							event: 'registration',
							authenticationMethod: 'email',
							userId: data.ID,
							email: data.email
						});
					} catch (error) {
						AppLogger.error(error);
					}
				}
			}
		},
		[track]
	);

	const update = useCallback(async (data) => {
		if (!isInit) {
			AppLogger.log('AppTracking not initialized. Skipping update.');
			return;
		}

		try {
			if (userflow.isIdentified()) {
				userflow.updateUser(data);
			}
		} catch {
			// do nothing
		}

		try {
			mixpanel.people.set(data);
		} catch {
			// do nothing
		}

		try {
			window.fcWidget?.user?.setProperties(data);
		} catch {
			// do nothing
		}
	}, []);

	const updateCompany = useCallback(async (data) => {
		if (!isInit) {
			AppLogger.log('AppTracking not initialized. Skipping update company.');
			return;
		}

		try {
			if (userflow.isIdentified()) {
				userflow.updateGroup(data);
			}
		} catch {
			// do nothing
		}

		try {
			mixpanel.people.set(data);
		} catch {
			// do nothing
		}

		try {
			window.fcWidget?.user?.setProperties(data);
		} catch {
			// do nothing
		}
	}, []);

	const updateOnboarding = useCallback(
		async (fieldArr) => {
			if (!Array.isArray(fieldArr)) {
				AppLogger.error('AppTracking updateOnboarding fieldArr must be an array!');
			} else if (fieldArr.length === 0) {
				AppLogger.error('AppTracking updateOnboarding fieldArr must not be empty!');
			} else if (fieldArr) {
				const instruction = store.getState().instruction;
				const user = store.getState().user;
				fieldArr.forEach((field) => {
					if (!instruction[field]) {
						const requestJSON = { [field]: true };
						if (field === 'shipmentCheck') {
							requestJSON.isOnboarded = true;
							track('1stShipped', {}, ['gtm']);
						} else if (field === 'secondShipmentCheck') {
							track('2ndShipped', {}, ['gtm']);
						}
						axios
							.post(ONBOARDING.UPDATE_ONBOARDING, requestJSON)
							.then((response) => {
								if (response.status === 200) {
									dispatch(updateInstructionData(requestJSON));
									update(requestJSON);
									track(field, { companyId: user?.companyId });
								} else {
									AppLogger.error(response);
								}
							})
							.catch((error) => {
								AppLogger.error(error);
							});
					} else {
						AppLogger.log('Instruction field already true: ', field);
					}
				});
			} else {
				AppLogger.error('AppTracking updateOnboarding fieldArr init error!');
			}
		},
		[dispatch, update]
	);

	const reset = useCallback(() => {
		if (!isInit) {
			AppLogger.log('AppTracking not initialized. Skipping reset.');
			return;
		}

		try {
			if (userflow.isIdentified()) {
				userflow.reset();
				window.dispatchEvent(new Event('userflow:reset'));
			}
		} catch {
			// do nothing
		}

		try {
			mixpanel.reset();
		} catch {
			// do nothing
		}

		try {
			window._cio?.reset?.();
		} catch {
			// do nothing
		}

		try {
			window.voiceflow?.chat?.destroy?.();
		} catch {
			// do nothing
		}

		try {
			init();
		} catch {
			// do nothing
		}
	}, []);

	return {
		init,
		identify,
		track,
		update,
		updateCompany,
		updateOnboarding,
		reset
	};
};

export default useAppTracking;
